/**
 * Right-rail patient preview in Spotlight: next appointment + outstanding balance.
 */

import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/shared/context/CurrencyContext';
import { useTimezone } from '@/shared/context/TimezoneContext';
import type { PatientSpotlightSummary } from './fetchPatientSpotlightSummary';

type PatientSpotlightSummaryPanelProps = {
  /** Null when no patient row is active — the rail stays reserved to avoid layout jump. */
  patientName: string | null;
  loading: boolean;
  error: boolean;
  summary: PatientSpotlightSummary | null;
  showNextAppointment: boolean;
  showOutstandingBalance: boolean;
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

export const PatientSpotlightSummaryPanel: React.FC<PatientSpotlightSummaryPanelProps> = ({
  patientName,
  loading,
  error,
  summary,
  showNextAppointment,
  showOutstandingBalance,
}) => {
  const { t } = useTranslation('common');
  const { formatCurrency } = useCurrency();
  const { formatOrgDate } = useTimezone();

  const nextLabel = (() => {
    if (!showNextAppointment) return null;
    if (!summary?.nextAppointmentAt) return t('globalSearchPatientSummaryNoAppointment');
    return (
      formatOrgDate(summary.nextAppointmentAt, 'dateTime') ||
      formatOrgDate(summary.nextAppointmentAt, 'date') ||
      t('globalSearchPatientSummaryNoAppointment')
    );
  })();

  const balanceLabel = (() => {
    if (!showOutstandingBalance) return null;
    if (summary?.outstandingBalance == null) return '—';
    return formatCurrency(summary.outstandingBalance);
  })();

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'block' },
        width: { sm: 220, md: 240 },
        minWidth: { sm: 200 },
        maxWidth: { sm: 260 },
        flexShrink: 0,
        borderLeft: '1px solid rgba(0, 0, 0, 0.06)',
        overflow: 'auto',
        py: 1.5,
        px: 2,
        bgcolor: 'grey.50',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          mb: 1,
        }}
      >
        {t('globalSearchPatientSummary')}
      </Typography>

      {!patientName ? (
        <Typography variant="body2" color="text.secondary">
          {t('globalSearchPatientSummaryEmpty')}
        </Typography>
      ) : (
        <>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {patientName}
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {!loading && error && (
            <Typography variant="body2" color="text.secondary">
              {t('globalSearchPatientSummaryError')}
            </Typography>
          )}

          {!loading && !error && (
            <>
              {showNextAppointment && nextLabel != null && (
                <Row label={t('globalSearchPatientSummaryNextAppointment')} value={nextLabel} />
              )}
              {showOutstandingBalance && balanceLabel != null && (
                <Row label={t('globalSearchPatientSummaryOutstanding')} value={balanceLabel} />
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};
